import React, { useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import TextField from 'core/components/validatedForm/TextField'
import Tooltip from 'core/elements/tooltip'
import { topMiddle } from 'core/elements/menu/defaults'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { remove, update } from 'ramda'
import { customValidator } from 'core/utils/fieldValidators'
import * as IpAddress from 'ip-address'
import { IPVersions } from 'app/constants'

const hostRouteValid = customValidator((value, formValues) => {
  if (!value) {
    return true
  }
  try {
    const [cidr, nexthop] = value.replace(/\s/g, '').split(',')
    const validCidr =
      formValues.ipVersion === IPVersions.IPv4
        ? new IpAddress.Address4(cidr).parsedSubnet
        : new IpAddress.Address6(cidr).parsedSubnet
    const validNexthop =
      formValues.ipVersion === IPVersions.IPv4
        ? new IpAddress.Address4(nexthop)
        : new IpAddress.Address6(nexthop)
    return !!validCidr && !!validNexthop
  } catch (err) {
    return false
  }
}, 'Must be a valid CIDR for the first value and a valid IP address for the second value, separated by a comma')

export default function HostRoutesField({ params, updateParams }) {
  const classes = useStyles()

  const addHostRoute = useCallback(() => {
    updateParams({ hostRoutes: [...params.hostRoutes, ''] })
  }, [params.hostRoutes])

  const removeHostRoute = useCallback(
    (index) => {
      updateParams({ hostRoutes: remove(index, 1, params.hostRoutes) })
    },
    [params.hostRoutes],
  )

  return (
    <div className={classes.subnetField}>
      <div className={classes.fieldLabel}>
        <Text variant="caption1">Host & Subnet Routes</Text>
        <Tooltip message="You may specify static routes to be provided to the VM via DHCP using the format: destination_cidr, nexthop. eg. 198.51.100.0/24, 192.0.2.1">
          <Text variant="inputLabel" className={classes.hint}>
            <FontAwesomeIcon>question-circle</FontAwesomeIcon>
          </Text>
        </Tooltip>
      </div>
      <div>
        {params.hostRoutes.map((hostRoute, index) => (
          <div key={index} className={classes.inputRow}>
            <TextField
              id={`hostRoutes.${index}`}
              label=""
              placeholder={`Route ${index + 1} (eg. 198.51.100.0/24, 192.0.2.1)`}
              value={hostRoute}
              onChange={(value) =>
                updateParams({ hostRoutes: update(index, value, params.hostRoutes) })
              }
              validations={[hostRouteValid]}
              required
            />
            <FontAwesomeIcon className={classes.minus} onClick={() => removeHostRoute(index)}>
              minus-circle
            </FontAwesomeIcon>
          </div>
        ))}
        <div className={classes.addEntry}>
          <FontAwesomeIcon className={classes.plus} onClick={() => addHostRoute()} size="lg">
            plus-circle
          </FontAwesomeIcon>
          <Text variant="body2" onClick={() => addHostRoute()} className={classes.clickable}>
            Add Host Route
          </Text>
        </div>
      </div>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  subnetField: {
    display: 'grid',
    gap: 8,
  },
  fieldLabel: {
    display: 'grid',
    maxWidth: 400,
    minWidth: 100,
    width: '100%',
    gridTemplateColumns: 'max-content minmax(0px, max-content)',
    gap: 8,
    alignItems: 'center',
  },
  hint: {
    cursor: 'help',
    transition: 'color .2s ease',
    color: theme.components.input.label.hint,
    '& i': {
      cursor: 'help',
      marginRight: 8,
      transition: 'color .2s ease',
      color: theme.components.input.label.hint,
    },
  },
  multipleInputs: {
    display: 'grid',
    gap: 12,
  },
  inputRow: {
    display: 'grid',
    gridTemplateColumns: '356px 32px',
    gridGap: '12px',
    justifyItems: 'start',
    alignItems: 'center',
  },
  minus: {
    fontWeight: 900,
    color: theme.palette.blue.main,
    justifySelf: 'end',
    position: 'relative',
    top: 4,
  },
  addEntry: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    marginTop: 12,
    marginLeft: 8,
    alignItems: 'center',
  },
  plus: {
    color: theme.palette.blue.main,
    marginRight: 8,
    fontWeight: 900,
  },
  clickable: {
    cursor: 'pointer',
  },
}))
